<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <BreadCrumb
          parentRouteName="CustomerLevelRuleList"
          :extra-items="isShow ? [
            {
              text: ruleBreadcrumbName,
            }
          ] : isEditing ? [
            {
              text: ruleBreadcrumbName,
              to: 'CustomerLevelRuleView',
              params: { id: customer_level_rule.id },
            },
            '編輯會員等級條件'
          ] : [
            '新增會員等級條件'
          ]"
        />
      </div>
    </div>

    <b-card>
      <b-overlay
        :show="isLoading"
        rounded
        opacity="0.6"
        spinner-small
        spinner-variant="primary"
      >
        <!-- 基本設定 -->
        <section class="row mb-5">
          <div class="col-12 col-xl-8">
            <h4 class="mb-4 text-primary">基本設定</h4>

            <b-form-group
              label-cols="12"
              label-cols-lg="2"
              label-size="sm"
              label="順序"
            >
              <b-form-input
                type="number"
                min="0"
                step="1"
                v-model.number="customer_level_rule.order"
                :state="v$.customer_level_rule.order.$error ? false : null"
                :disabled="isShow"
              ></b-form-input>
              <b-form-invalid-feedback :state="!v$.customer_level_rule.order.$error">
                請輸入整數
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group
              label-cols="12"
              label-cols-lg="2"
              label-size="sm"
              label="類型"
              label-for="type"
            >
              <b-form-select
                id="type"
                class="mb-2 mr-sm-2 mb-sm-0"
                v-model="customer_level_rule.type"
                :options="customerLevelTypeOptions"
                :state="v$.customer_level_rule.type.$error ? false : null"
                :disabled="isShow"
              ></b-form-select>
            </b-form-group>

            <b-form-group
              label-cols="12"
              label-cols-lg="2"
              label-size="sm"
              label="原始等級"
              label-for="from-level"
            >
              <b-form-select
                id="from-level"
                class="mb-2 mr-sm-2 mb-sm-0"
                v-model="customer_level_rule.from_customer_level_id"
                :options="customerLevelOptions.slice(0,['upgrade','direct'].includes(customer_level_rule.type) ? -1 : customerLevelOptions.length)"
                :disabled="isShow"
              ></b-form-select>
              <small class="form-text text-muted">
                選擇性設定：指定從哪個會員等級升級而來
              </small>
            </b-form-group>

            <b-form-group
              label-cols="12"
              label-cols-lg="2"
              label-size="sm"
              label="目標等級"
              label-for="target-level"
            >
              <b-form-select
                id="target-level"
                class="mb-2 mr-sm-2 mb-sm-0"
                v-model="customer_level_rule.customer_level_id"
                :options="filteredTargetLevelOptions"
                :state="v$.customer_level_rule.customer_level_id.$error ? false : null"
                :disabled="isShow || !customer_level_rule.type || ['upgrade','keep'].includes(customer_level_rule.type)"
              ></b-form-select>
              <b-form-invalid-feedback :state="!v$.customer_level_rule.customer_level_id.$error">
                此欄位為必填
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group
              label-cols="12"
              label-cols-lg="2"
              label-size="sm"
              label="是否啟用"
            >
              <b-form-checkbox
                v-model="customer_level_rule.is_enabled"
                :disabled="isShow"
              ></b-form-checkbox>
            </b-form-group>

            <b-form-group
              v-if="customer_level_rule.type === 'keep'"
              label-cols="12"
              label-cols-lg="2"
              label-size="sm"
              label="續等時沿用原會籍起始日"
            >
              <b-form-checkbox
                v-model="rule.keep_original_start"
                :disabled="isShow"
              >
                是
              </b-form-checkbox>
              <small class="form-text text-muted">
                續等過後，若要沿用原會籍期間之消費紀錄，則需啟用此項目
              </small>
            </b-form-group>
          </div>
        </section>

        <!-- 時間範圍設定 -->
        <section class="row mb-5">
          <div class="col-12 col-xl-8">
            <h4 class="mb-4 text-primary">時間範圍</h4>

            <b-form-group
              label-cols="12"
              label-cols-lg="2"
              label-size="sm"
              label="範圍類型"
            >
              <b-form-select
                v-model="rule.time_range.type"
                :options="timeRangeTypeOptions"
                :disabled="isShow"
              ></b-form-select>
            </b-form-group>

            <!-- custom_range: 自訂日期區間 -->
            <b-form-group
              v-if="rule.time_range.type === 'custom_range'"
              label-cols="12"
              label-cols-lg="2"
              label-size="sm"
              label="日期區間"
            >
              <div class="d-flex align-items-center">
                <b-form-input
                  type="date"
                  v-model="rule.time_range.start"
                  :disabled="isShow"
                  class="mr-2"
                ></b-form-input>
                <span class="mx-2">至</span>
                <b-form-input
                  type="date"
                  v-model="rule.time_range.end"
                  :disabled="isShow"
                ></b-form-input>
              </div>
            </b-form-group>

            <!-- rolling_days: 滾動天數 -->
            <b-form-group
              v-if="rule.time_range.type === 'rolling_days'"
              label-cols="12"
              label-cols-lg="2"
              label-size="sm"
              label="天數"
            >
              <b-input-group append="天內">
                <b-form-input
                  type="number"
                  min="1"
                  v-model.number="rule.time_range.days"
                  :disabled="isShow"
                ></b-form-input>
              </b-input-group>
              <small class="form-text text-muted">計算過去 N 天內的資料</small>
            </b-form-group>

            <b-form-group
              label-cols="12"
              label-cols-lg="2"
              label-size="sm"
              label="會籍起始日"
            >
              <b-form-checkbox
                v-model="rule.use_membership_start_date"
                :disabled="isShow"
              >
                從會員等級起始時間開始計算
              </b-form-checkbox>
              <small class="form-text text-muted">
                勾選：取「會籍開始日」與「時間範圍開始日」較晚者<br>
                不勾選：直接使用上方設定的時間範圍
              </small>
            </b-form-group>
          </div>
        </section>

        <!-- 計算條件設定 -->
        <section class="row mb-5">
          <div class="col-12 col-xl-10">
            <h4 class="mb-4 text-primary">計算條件</h4>

            <!-- 多條件邏輯 -->
            <b-form-group
              v-if="rule.calculations.length > 1"
              label-cols="12"
              label-cols-lg="2"
              label-size="sm"
              label="條件邏輯"
            >
              <b-form-radio-group
                v-model="rule.calculation_logic"
                :options="calculationLogicOptions"
                :disabled="isShow"
              ></b-form-radio-group>
              <small class="form-text text-muted">
                AND：所有條件都要滿足 / OR：滿足任一條件即可
              </small>
            </b-form-group>

            <!-- 條件列表 -->
            <div
              v-for="(calc, index) in rule.calculations"
              :key="index"
              class="card mb-3"
            >
              <div class="card-header d-flex justify-content-between align-items-center py-2">
                <span class="font-weight-bold">條件 {{ index + 1 }}</span>
                <b-button
                  v-if="!isShow && rule.calculations.length > 1"
                  variant="outline-danger"
                  size="sm"
                  @click="removeCalculation(index)"
                >
                  <i class="fa fa-trash"></i>
                </b-button>
              </div>
              <div class="card-body">
                <b-form-group
                  label-cols="12"
                  label-cols-lg="2"
                  label-size="sm"
                  label="策略類型"
                >
                  <b-form-select
                    v-model="calc.strategy"
                    :options="strategyOptions"
                    :disabled="isShow"
                    @change="onStrategyChange(index)"
                  ></b-form-select>
                </b-form-group>

                <!-- order_sum: 訂單累積金額 -->
                <template v-if="calc.strategy === 'order_sum'">
                  <b-form-group
                    label-cols="12"
                    label-cols-lg="2"
                    label-size="sm"
                    label="累積金額"
                  >
                    <b-input-group prepend="NT$">
                      <b-form-input
                        type="text"
                        v-model="calc.amountFormatted"
                        :disabled="isShow"
                        @blur="formatAmount(calc)"
                      ></b-form-input>
                    </b-input-group>
                  </b-form-group>
                </template>

                <!-- order_single_max: 單筆消費達標 -->
                <template v-if="calc.strategy === 'order_single_max'">
                  <b-form-group
                    label-cols="12"
                    label-cols-lg="2"
                    label-size="sm"
                    label="單筆金額"
                  >
                    <b-input-group prepend="NT$">
                      <b-form-input
                        type="text"
                        v-model="calc.amountFormatted"
                        :disabled="isShow"
                        @blur="formatAmount(calc)"
                      ></b-form-input>
                    </b-input-group>
                    <small class="form-text text-muted">單筆訂單達到此金額即符合條件</small>
                  </b-form-group>
                </template>

                <!-- order_count: 消費次數 -->
                <template v-if="calc.strategy === 'order_count'">
                  <b-form-group
                    label-cols="12"
                    label-cols-lg="2"
                    label-size="sm"
                    label="消費次數"
                  >
                    <b-input-group append="次">
                      <b-form-input
                        type="number"
                        min="1"
                        v-model.number="calc.count"
                        :disabled="isShow"
                      ></b-form-input>
                    </b-input-group>
                  </b-form-group>
                </template>

                <!-- order_count_min_amount: 消費次數且每次滿額 -->
                <template v-if="calc.strategy === 'order_count_min_amount'">
                  <b-form-group
                    label-cols="12"
                    label-cols-lg="2"
                    label-size="sm"
                    label="消費次數"
                  >
                    <b-input-group append="次">
                      <b-form-input
                        type="number"
                        min="1"
                        v-model.number="calc.count"
                        :disabled="isShow"
                      ></b-form-input>
                    </b-input-group>
                  </b-form-group>
                  <b-form-group
                    label-cols="12"
                    label-cols-lg="2"
                    label-size="sm"
                    label="每次最低金額"
                  >
                    <b-input-group prepend="NT$">
                      <b-form-input
                        type="text"
                        v-model="calc.minAmountFormatted"
                        :disabled="isShow"
                        @blur="formatMinAmount(calc)"
                      ></b-form-input>
                    </b-input-group>
                    <small class="form-text text-muted">每次消費須達到此金額才計入次數</small>
                  </b-form-group>
                </template>

                <!-- point_balance: 點數餘額 -->
                <template v-if="calc.strategy === 'point_balance'">
                  <b-form-group
                    label-cols="12"
                    label-cols-lg="2"
                    label-size="sm"
                    label="點數門檻"
                  >
                    <b-input-group append="點">
                      <b-form-input
                        type="number"
                        min="0"
                        v-model.number="calc.amount"
                        :disabled="isShow"
                      ></b-form-input>
                    </b-input-group>
                  </b-form-group>
                </template>

                <!-- form_result_count: 表單填寫次數 -->
                <template v-if="calc.strategy === 'form_result_count'">
                  <b-form-group
                    label-cols="12"
                    label-cols-lg="2"
                    label-size="sm"
                    label="填寫次數"
                  >
                    <b-input-group append="次">
                      <b-form-input
                        type="number"
                        min="1"
                        v-model.number="calc.count"
                        :disabled="isShow"
                      ></b-form-input>
                    </b-input-group>
                  </b-form-group>
                </template>

                <!-- 訂單狀態過濾 (針對訂單相關策略) - 暫時隱藏，尚未實作 -->
                <!-- <b-form-group
                  v-if="isOrderStrategy(calc.strategy)"
                  label-cols="12"
                  label-cols-lg="2"
                  label-size="sm"
                  label="訂單狀態"
                >
                  <b-form-checkbox-group
                    v-model="calc.filters.status"
                    :options="orderStatusOptions"
                    :disabled="isShow"
                  ></b-form-checkbox-group>
                  <small class="form-text text-muted">
                    不勾選則不過濾訂單狀態
                  </small>
                </b-form-group> -->
              </div>
            </div>

            <!-- 新增條件按鈕 - 暫時隱藏，前期不需要支援多條件 -->
            <!-- <b-button
              v-if="!isShow"
              variant="outline-primary"
              @click="addCalculation"
              class="mt-2"
            >
              <i class="fa fa-plus mr-1"></i> 新增條件
            </b-button> -->
          </div>
        </section>
      </b-overlay>

      <div class="d-flex justify-content-center">
        <b-button class="mr-4" variant="outline-danger" @click="handleCancel">
          返回
        </b-button>
        <b-overlay
          :show="isLoading && !isShow"
          rounded
          opacity="0.6"
          spinner-small
          spinner-variant="primary"
          class="d-inline-block"
          v-if="!isShow"
        >
          <b-button variant="success" @click="handleSubmit">
            {{ isEditing ? "確認修改" : "確認新增" }}
          </b-button>
        </b-overlay>
      </div>
    </b-card>
  </div>
</template>

<script>
import useVuelidate from "@vuelidate/core";
import { integer, numeric, required } from "@vuelidate/validators";
import { mapGetters, mapState } from "vuex";
import customerLevelRuleApi from "@/apis/customer-level-rules";
import customerLevelApi from "@/apis/customer-levels";
import BreadCrumb from "@/components/Dashboard/BreadCrumb";

export default {
  components: {
    BreadCrumb,
  },
  setup() {
    const v$ = useVuelidate();
    return { v$ };
  },
  data() {
    return {
      isLoading: false,
      isInitializing: true,
      customerLevelOptions: [],

      customerLevelTypeOptions: [
        { value: "upgrade", text: "升等" },
        { value: "keep", text: "續等" },
        { value: "direct", text: "直升" },
      ],

      timeRangeTypeOptions: [
        { value: "forever", text: "無限期" },
        { value: "annual_period", text: "當年度 (1/1 ~ 12/31)" },
        { value: "monthly_period", text: "當月" },
        { value: "rolling_days", text: "滾動天數" },
        { value: "custom_range", text: "自訂日期區間" },
      ],

      calculationLogicOptions: [
        { value: "AND", text: "AND (全部滿足)" },
        { value: "OR", text: "OR (任一滿足)" },
      ],

      strategyOptions: [
        { value: "order_sum", text: "訂單累積金額" },
        { value: "order_single_max", text: "單筆消費達標" },
        { value: "order_count", text: "消費次數" },
        { value: "order_count_min_amount", text: "消費次數且每次滿額" },
        { value: "point_balance", text: "點數餘額" },
        { value: "form_result_count", text: "表單填寫次數" },
      ],

      orderStatusOptions: [
        { value: "completed", text: "已完成" },
        { value: "shipped", text: "已出貨" },
        { value: "paid", text: "已付款" },
      ],

      customer_level_rule: {
        customer_level_id: null,
        from_customer_level_id: null,
        type: "upgrade",
        order: 0,
        is_enabled: true,
      },

      rule: {
        time_range: {
          type: "forever",
          start: null,
          end: null,
          days: 365,
        },
        use_membership_start_date: true,
        calculation_logic: "AND",
        calculations: [
          this.createDefaultCalculation(),
        ],
        keep_original_start: false,
      },
    };
  },

  validations() {
    return {
      customer_level_rule: {
        customer_level_id: { required },
        type: {},
        order: { integer, numeric },
        is_enabled: { required },
      },
    };
  },

  async mounted() {
    await this.getCustomerLevelsList();

    if (this.$route.query.level_id) {
      this.customer_level_rule.customer_level_id = this.$route.query.level_id;
    }

    if (this.isEditing || this.isShow) {
      await this.getRule();
    } else {
      const firstOption = this.customerLevelOptions[0];
      if (firstOption) {
        this.customer_level_rule.from_customer_level_id = firstOption.value;
      }
      // 初始化預設計算條件
      this.rule.calculations = [this.createDefaultCalculation()];
    }

    this.isInitializing = false;
  },

  computed: {
    ...mapState("general", {
      organization: (state) => state.organization,
    }),
    ...mapGetters("route", ["routeQuery"]),

    isShow() {
      return this.$route.name === "CustomerLevelRuleView";
    },

    isEditing() {
      return this.$route.name === "CustomerLevelRuleEdit";
    },

    filteredTargetLevelOptions() {
      if (this.customer_level_rule.type === 'direct') {
        const fromIndex = this.customerLevelOptions.findIndex(
          option => option.value === this.customer_level_rule.from_customer_level_id
        );
        return this.customerLevelOptions.slice(fromIndex + 1);
      }
      return this.customerLevelOptions;
    },

    ruleBreadcrumbName() {
      const fromLevel = this.customerLevelOptions.find(
        option => option.value === this.customer_level_rule.from_customer_level_id
      );
      const toLevel = this.customerLevelOptions.find(
        option => option.value === this.customer_level_rule.customer_level_id
      );

      const fromName = fromLevel ? fromLevel.text : '-';
      const toName = toLevel ? toLevel.text : '-';

      return `${fromName} → ${toName}`;
    },
  },

  watch: {
    'customer_level_rule.type'() {
      if (this.isInitializing) return;

      const firstOption = this.customerLevelOptions[0];
      if (!firstOption) return;

      this.customer_level_rule.from_customer_level_id = firstOption.value;

      if (this.customer_level_rule.type === 'upgrade') {
        const nextOption = this.customerLevelOptions
          .filter(option => option.order > firstOption.order)
          .sort((a, b) => a.order - b.order)[0];
        this.customer_level_rule.customer_level_id = nextOption ? nextOption.value : null;
      } else if (this.customer_level_rule.type === 'keep') {
        this.customer_level_rule.customer_level_id = this.customer_level_rule.from_customer_level_id;
      }
    },

    'customer_level_rule.from_customer_level_id'(newValue) {
      if (this.isInitializing) return;

      if (['upgrade', 'direct'].includes(this.customer_level_rule.type)) {
        const currentOption = this.customerLevelOptions.find(option => option.value === newValue);
        if (!currentOption || typeof currentOption.order === 'undefined') return;

        const nextOption = this.customerLevelOptions
          .filter(option => option.order > currentOption.order)
          .sort((a, b) => a.order - b.order)[0];
        this.customer_level_rule.customer_level_id = nextOption?.value ?? null;
      } else if (this.customer_level_rule.type === 'keep') {
        this.customer_level_rule.customer_level_id = newValue;
      }
    },
  },

  methods: {
    createDefaultCalculation() {
      return {
        strategy: "order_sum",
        amount: 0,
        amountFormatted: "0",
        count: 0,
        min_amount: 0,
        minAmountFormatted: "0",
        field: "total",
        filters: {
          status: [],
        },
      };
    },

    async getCustomerLevelsList() {
      try {
        this.isLoading = true;
        const { data } = await customerLevelApi.list({ all: true });
        this.customerLevelOptions = data.data.map((item) => ({
          value: item.id,
          text: item.name,
          order: item.order,
        }));
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    async getRule() {
      try {
        this.isLoading = true;
        const response = await customerLevelRuleApi.getRule(
          this.organization,
          this.$route.params.id
        );

        const data = response.data.data;
        this.customer_level_rule = {
          id: data.id,
          customer_level_id: data.customer_level_id,
          from_customer_level_id: data.from_customer_level_id,
          type: data.type,
          order: data.order,
          is_enabled: data.is_enabled,
        };

        // 解析 rule JSON
        if (data.rule) {
          this.rule = this.parseRuleFromApi(data.rule);
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    parseRuleFromApi(apiRule) {
      const rule = {
        time_range: apiRule.time_range || { type: "forever" },
        use_membership_start_date: apiRule.use_membership_start_date ?? true,
        calculation_logic: apiRule.calculation_logic || "AND",
        calculations: [],
        keep_original_start: apiRule.keep_original_start ?? false,
      };

      // 解析 calculations
      if (apiRule.calculations && apiRule.calculations.length > 0) {
        rule.calculations = apiRule.calculations.map(calc => {
          const parsed = {
            ...this.createDefaultCalculation(),
            ...calc,
          };

          // 格式化金額顯示
          if (parsed.amount) {
            parsed.amountFormatted = this.formatNumberDisplay(parsed.amount);
          }
          if (parsed.min_amount) {
            parsed.minAmountFormatted = this.formatNumberDisplay(parsed.min_amount);
          }

          // 確保 filters 存在
          if (!parsed.filters) {
            parsed.filters = { status: [] };
          }
          if (!parsed.filters.status) {
            parsed.filters.status = [];
          }

          return parsed;
        });
      } else {
        rule.calculations = [this.createDefaultCalculation()];
      }

      return rule;
    },

    prepareRuleForApi() {
      const calculations = this.rule.calculations.map(calc => {
        const prepared = {
          strategy: calc.strategy,
        };

        // 根據策略類型添加對應欄位
        switch (calc.strategy) {
          case "order_sum":
          case "order_single_max":
            prepared.amount = this.parseFormattedNumber(calc.amountFormatted);
            prepared.field = calc.field || "total";
            break;
          case "order_count":
            prepared.count = calc.count;
            break;
          case "order_count_min_amount":
            prepared.count = calc.count;
            prepared.min_amount = this.parseFormattedNumber(calc.minAmountFormatted);
            prepared.field = calc.field || "total";
            break;
          case "point_balance":
            prepared.amount = calc.amount;
            break;
          case "form_result_count":
            prepared.count = calc.count;
            break;
        }

        // 添加 filters（如果有設定）
        if (calc.filters && calc.filters.status && calc.filters.status.length > 0) {
          prepared.filters = {
            status: calc.filters.status,
          };
        }

        return prepared;
      });

      const rule = {
        time_range: { ...this.rule.time_range },
        use_membership_start_date: this.rule.use_membership_start_date,
        calculation_logic: this.rule.calculation_logic,
        calculations,
        keep_original_start: this.rule.keep_original_start,
      };

      // 清理 time_range 中不需要的欄位
      if (rule.time_range.type === "forever" ||
          rule.time_range.type === "annual_period" ||
          rule.time_range.type === "monthly_period") {
        delete rule.time_range.start;
        delete rule.time_range.end;
        delete rule.time_range.days;
      } else if (rule.time_range.type === "rolling_days") {
        delete rule.time_range.start;
        delete rule.time_range.end;
      } else if (rule.time_range.type === "custom_range") {
        delete rule.time_range.days;
      }

      return rule;
    },

    addCalculation() {
      this.rule.calculations.push(this.createDefaultCalculation());
    },

    removeCalculation(index) {
      if (this.rule.calculations.length > 1) {
        this.rule.calculations.splice(index, 1);
      }
    },

    onStrategyChange(index) {
      // 重置該條件的數值
      const calc = this.rule.calculations[index];
      calc.amount = 0;
      calc.amountFormatted = "0";
      calc.count = 0;
      calc.min_amount = 0;
      calc.minAmountFormatted = "0";
      calc.filters = { status: [] };
    },

    isOrderStrategy(strategy) {
      return [
        "order_sum",
        "order_single_max",
        "order_count",
        "order_count_min_amount",
      ].includes(strategy);
    },

    formatAmount(calc) {
      const num = this.parseFormattedNumber(calc.amountFormatted);
      calc.amount = num;
      calc.amountFormatted = this.formatNumberDisplay(num);
    },

    formatMinAmount(calc) {
      const num = this.parseFormattedNumber(calc.minAmountFormatted);
      calc.min_amount = num;
      calc.minAmountFormatted = this.formatNumberDisplay(num);
    },

    formatNumberDisplay(num) {
      if (num === null || num === undefined) return "0";
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },

    parseFormattedNumber(str) {
      if (!str) return 0;
      const num = Number(str.toString().replace(/,/g, ""));
      return isNaN(num) ? 0 : num;
    },

    async handleSubmit() {
      try {
        const result = await this.v$.$validate();
        if (!result) return;

        this.isLoading = true;

        const data = {
          rule: this.prepareRuleForApi(),
          customer_level_id: this.customer_level_rule.customer_level_id,
          from_customer_level_id: this.customer_level_rule.from_customer_level_id,
          type: this.customer_level_rule.type,
          order: this.customer_level_rule.order,
          is_enabled: this.customer_level_rule.is_enabled,
        };

        if (this.isEditing) {
          data.id = this.customer_level_rule.id;
          await customerLevelRuleApi.updateRule(this.organization, data);
        } else {
          await customerLevelRuleApi.createRule(this.organization, data);
        }

        this.$router.push({
          name: "CustomerLevelRuleList",
          query: this.routeQuery,
        });
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", "", "error");
      } finally {
        this.isLoading = false;
      }
    },

    handleCancel() {
      this.$router.push({
        name: "CustomerLevelRuleList",
        query: this.routeQuery,
      });
    },
  },
};
</script>

<style scoped>
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
}

.card {
  border: 1px solid #dee2e6;
}

.card-header {
  background-color: #f8f9fa;
}
</style>
